import { ZodRealError } from 'zod';

import {
  ApiErrorDetails,
  ApiNoLiquidityAvailableError,
  ErrorContext,
  createApiError,
} from '@/lib/errors/api';
import { ClientForbiddenError, ClientNetworkError } from '@/lib/errors/client';
import { addQuoteJwtHeader, clearQuoteJwt } from '@/lib/jwt/client';

import * as environment from './environment';
import { HttpError } from './http-error';
import { routes } from './routes';

const PROTECTED_PATHS = [
  routes.api.SWAP_QUOTE,
  routes.api.INTENTS_QUOTE,
  routes.api.SWAP_PRICE,
  routes.api.INTENTS_PRICE,
  routes.api.CROSS_CHAIN_QUOTE,
  routes.api.USD_PRICES,
] as const;

/**
 * Fetcher functions used by React Query hooks across the app.
 *
 * @remarks
 * Next.js requires that `fetch` in a test env uses an absolute URL. This is
 * because the application doesn't know what context this code will be called in
 * (either in the browser on production or in node when running tests/SSR), so
 * the implementation of `fetch` can vary between them.
 */

/**
 * Helper function to get the full URL.
 */
export const getFullURL = (path: string) => {
  const baseUrl = environment.isTest ? environment.getBaseURL() : '';
  return `${baseUrl}${path}`;
};

/**
 * Helper function to handle the response.
 */
export const handleResponse = async <T>(r: Response): Promise<T> => {
  if (!r.ok) throw new ClientNetworkError(await r.text());
  return r.json();
};

/**
 * Default fetcher for GET requests.
 */
export const fetcher = async <T>(path: string) => fetch(getFullURL(path)).then(handleResponse<T>);

/**
 * Reads a cloned response's JSON without consuming the original body. Returns an
 * empty object if the body isn't JSON, so callers can still read the real response.
 */
const peekJson = async (response: Response): Promise<{ code?: unknown; kind?: unknown }> => {
  try {
    return await response.clone().json();
  } catch {
    return {};
  }
};

/**
 * Only 403s are peeked, so successful streamed responses return without being read.
 */
const isJwtGate403 = async (response: Response) => {
  if (response.status !== 403) return false;
  const data = await peekJson(response);
  return data?.code === 403 && data?.kind === 'jwt';
};

/**
 * Attaches the quote JWT, performs the fetch, and on a JWT-gate 403 clears the
 * cached token, mints a fresh one, and replays the request **exactly once**.
 *
 */
const fetchWithJwtRetry = async (input: string, init?: RequestInit): Promise<Response> => {
  const attempt = async () => {
    const headers = await addQuoteJwtHeader(init?.headers);
    return fetch(input, { ...init, headers });
  };

  const response = await attempt();
  if (!(await isJwtGate403(response))) return response;

  clearQuoteJwt();
  const replayed = await attempt();
  if (await isJwtGate403(replayed)) {
    throw new ClientForbiddenError('jwt');
  }

  return replayed;
};

/**
 * Fetcher for GET requests that require JWT authentication.
 *
 * @remarks
 * Recovers from a network-binding 403 by re-minting and replaying once
 * (see {@link fetchWithJwtRetry}).
 */
export const fetcherWithJwt = async <T>(path: string) =>
  fetchWithJwtRetry(getFullURL(path)).then(handleResponse<T>);

/**
 * Fetch (returning the raw `Response`) for requests that require JWT authentication.
 *
 * @remarks
 * Recovers from a network-binding 403 by re-minting and replaying once
 * (see {@link fetchWithJwtRetry}).
 */
export const fetchWithJwt = async (input: string, init?: RequestInit) =>
  fetchWithJwtRetry(input, init);

/**
 * Default fetcher for POST requests.
 */
export const postData = async <T>(path: string, { arg }: { arg: unknown }) =>
  fetch(getFullURL(path), {
    method: 'POST',
    body: JSON.stringify(arg),
    headers: { 'Content-Type': 'application/json' },
  }).then(handleResponse<T>);

/**
 * API paths which require a quote JWT header to be verified.
 */
const isProtectedQuotePath = (path: string) =>
  PROTECTED_PATHS.some((protectedPath) => path.startsWith(protectedPath));

/**
 * Fetcher for GET requests to 0x API.
 *
 * @remarks
 * This fetcher is used to handle errors from the 0x API. It first checks for a
 * Zod error, then an API error, and finally throws an `Error`. If requesting a swap
 * or gasless quote, it attaches a required quote JWT verified on the server via
 * {@link fetchWithJwtRetry}, which transparently re-mints + replays once on a
 * network-binding 403 and throws {@link ClientForbiddenError} if still gated.
 */
export const zeroExApiFetcher = async (path: string, context?: ErrorContext) => {
  const url = getFullURL(path);

  // Quote hooks run in the browser; only attach the JWT client-side. The retry
  // primitive handles the gate-403 re-mint; plain fetch is used off the JWT path.
  const needsJwt = typeof window !== 'undefined' && isProtectedQuotePath(path);
  const response = needsJwt ? await fetchWithJwtRetry(url) : await fetch(url);

  if (response.status === 204) throw new ApiNoLiquidityAvailableError();

  const data = await response.json();

  if (!response.ok) {
    // A persistent JWT-gate 403 has already thrown ClientForbiddenError('jwt')
    // inside fetchWithJwtRetry, so it never reaches this block.
    if (data.code === 400) {
      if ('issues' in data) {
        // Match parse() errors: the base ZodError constructor does not extend Error in v4.
        throw new ZodRealError(data.issues);
      } else if ('reason' in data) {
        throw new HttpError(400, data, context);
      }
    } else if ('data' in data && 'name' in data && 'message' in data) {
      throw createApiError(data as ApiErrorDetails, { context, status: response.status });
    }
    throw new Error(data.message);
  }

  return data;
};
